import _ from 'lodash';
import moment from 'moment/moment';
import {calculateDifferenceInMinutes} from '../../bedManagement/bmUtils';
import {getSessionSlotDuration} from '../components/utils/sessionUtilities';
import {TIMER_COUNTDOWN_MINUTES} from './constants/appointmentSessionConstants';

export const DEFAULT_SLOT_DURATION_IN_MINUTES = 30;

////////////////////////////////////////////////
////             PT Data             ////
///////////////////////////////////////////////

export const getReviewData = (pt, isED = true) => isED && pt?.bedas;

export const getSessionId = (session) => session?.sessionId || session?.id;

export const isSessionSingleEvent = (session) =>
  session?.singleEvent || !!session?.from || !!session?.to;

////////////////////////////////////////////////
////          TIME UTILS          ////
///////////////////////////////////////////////

export const cleanHour = (date, format) =>
  moment(date, format && format).set({ hours: 0, minutes: 0, seconds: 0 });

export const cleanDate = (date, format) =>
  moment().set({
    hours: moment(date, format && format).hours(),
    minutes: moment(date, format && format).minutes(),
    second: 0,
    millisecond: 0,
  });

const comparedMinutesAreSame = (date1, date2) =>
  moment(cleanDate(date1)).isSame(cleanDate(date2), "minutes");

export const customSessionTimeDiffers = (sessionState, customDayHourObj) => {
  const { startTime: startCustomDate, endTime: endCustomDate } =
    customDayHourObj;
  const { startTime: startDefaultDate, endTime: endDefaultDate } = sessionState;

  return (
    !comparedMinutesAreSame(startCustomDate, startDefaultDate) ||
    !comparedMinutesAreSame(endCustomDate, endDefaultDate)
  );
};

export const cleanCustomSessionTimeArray = (sessionState) => {
  const customClone = _.cloneDeep(sessionState.customSessionTime);
  for (let key in customClone) {
    const val = customClone[key];

    if (!customSessionTimeDiffers(sessionState, val)) {
      delete customClone[key];
    }
  }
  return customClone;
};

export const getSessionName = (session) => session?.sessionName;

export const getSessionStartHour = (
  sessionActive,
  session,
  dayOfWeek,
  isSingle = false,
) => {
  if (!sessionActive) return "-";

  let startDate;

  if (isSingle) {
    startDate = session?.from || session?.startDate;
  } else if (
    session?.customSessionTime &&
    session?.customSessionTime?.[dayOfWeek]
  ) {
    startDate = moment(
      session?.customSessionTime?.[dayOfWeek]?.startTime,
      "HH:mm",
    );
  } else {
    if (session?.slotStartTime) {
      const slotStartTime = moment.utc(session.slotStartTime, "HH:mm").local();

      startDate = moment(session.startDate).local().set({
        minute: slotStartTime.minute(),
        hour: slotStartTime.hour(),
      });
    } else {
      startDate = session?.startDate;
    }
  }

  return moment(startDate);
};

export const getSessionEndHour = (
  sessionActive,
  session,
  dayOfWeek,
  isSingle = false,
) => {
  if (!sessionActive) {
    return "-";
  }

  let endDate;

  if (isSingle) {
    endDate = session?.to || session?.endDate;
  } else if (
    session?.customSessionTime &&
    session?.customSessionTime?.[dayOfWeek]
  ) {
    // endDate = moment.utc(session?.customSessionTime?.[dayOfWeek]?.endTime, "HH:mm");
    endDate = moment(session?.customSessionTime?.[dayOfWeek]?.endTime, "HH:mm");
  } else {
    endDate = session?.endDate;
  }

  return moment(endDate);
};

export const getStartDate = (event, isReview, date, isSingle, isBars) =>
  isBars
    ? moment(event?.start).toISOString()
    : isReview
      ? getReviewData(event)?.date
      : getSessionStartHour(
          true,
          event,
          moment(date, "DD/MM/YYYY").isoWeekday(),
          isSingle,
        ) ||
        event?.startDate ||
        moment(event?.start || event?.from || event?.date).toISOString();

export const getEndDate = (event, isReview, date, isSingle, isBars) =>
  isBars
    ? moment(event?.end).toISOString()
    : isReview
      ? moment(getReviewData(event)?.date).add(30, "minutes").toISOString()
      : getSessionEndHour(
          true,
          event,
          moment(date, "DD/MM/YYYY").isoWeekday(),
          isSingle,
        ) ||
        event?.endDate ||
        moment(event?.end || event?.to).toISOString();

export const calculateDuration = (start, end, isSameDay) => {
  const endDate = !isSameDay
    ? moment(end)
    : moment(start).set({
        hour: moment(end).hours(),
        minute: moment(end).minutes(),
      });
  return moment.duration(moment(endDate).diff(moment(start))).asMinutes();
};

export const getTimerFontColor = (countdownTo) => {
  const difference = -calculateDifferenceInMinutes(countdownTo);

  if (difference > TIMER_COUNTDOWN_MINUTES.yellow) {
    return "green";
  } else if (
    difference < TIMER_COUNTDOWN_MINUTES.yellow &&
    difference > TIMER_COUNTDOWN_MINUTES.red
  ) {
    return "yellow";
  } else {
    return "red";
  }
};

////////////////////////////////////////////////
//// EVENT POSITIONING  ////
///////////////////////////////////////////////

// Swimlane options
export const SWIMLANE_COLUMN_WIDTH = 400;
export const SLOT_HEIGHT = 110;
export const DEFAULT_LEFT = 70;
export const LEFT_OFFSET = 2;
export const OFFSET = 2;
export const COLUMN_GAP = 8;
export const OFFSET_LEFT = 0;
export const BUFFER_COLUMNS = 1;

export const calculateEventHeight = ({
  event,
  eventDate,
  isReview,
  isBars,
  parentSession,
}) => {
  const durationInMinutes =
    parentSession || event?.singleEvent
      ? getSessionSlotDuration(parentSession || event)
      : calculateDuration(
          getStartDate(event, isReview, eventDate, false, isBars),
          getEndDate(event, isReview, eventDate, false, isBars),
          true,
        );

  let height = (durationInMinutes / 30) * SLOT_HEIGHT;

  return `${height}px`;
};

export const calculateSessionHeight = ({
  event,
}) => {
  const durationInMinutes = calculateDuration(
    event.startDate,
    event.endDate,
    true,
  );

  let height = (durationInMinutes / 30) * SLOT_HEIGHT - 2 * OFFSET;
  height -= 1 * OFFSET;

  return `${height}px`;
};

export const calculateEventTop = ({
  event,
}) => {
  const startDate = event?.startDate;
  const startOfDay = moment(startDate).startOf("day");
  const minutesFromStartOfDay = moment(startDate).diff(startOfDay, "minutes");
  let top = (minutesFromStartOfDay / 30) * SLOT_HEIGHT;

  return `${top}px`;
};

// export const getAssignedSlotTileId = (appointment) => `${appointment?.assignedSession}${moment(appointment?.startDate).toISOString()}`;
export const getAssignedSlotTileId = (appointment) => appointment?.__id ? appointment?.__id : `${appointment?.assignedSession}${moment(appointment?.startDate).toISOString()}`;